import dayjs, { Dayjs } from 'dayjs'
import React, { useEffect, useMemo, useRef, useState, Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, CircularProgress, Collapse, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'
import { FilterList } from '@mui/icons-material'
import DateFormat from '../../store/types/enums/DateFormat'
import { capitalizeMonthName } from '../../store/utils/dateUtils'
import FilterModificationHistoryMenu from '../FilterMenu/FilterModificationHistoryMenu'
import parseJsonSafe from '../Schedule/parseJsonSafe'
import {
  ModificationHistoryParams,
  OperationType,
  useGetModificationHistoryQuery,
  History,
} from '../../store/redux/reducers/auditSlice'
import { User } from '../../store/types'

interface BaseProps {
  selectedDate: Dayjs
  onLeaveButtonClick: () => void
  selected: User | null
  tableName: string
  filterFn: (item: History, selectedDate: Dayjs) => boolean
  renderRows: (date: string, filteredAuditData: History[], selectedDate: Dayjs) => React.ReactNode
  title: string
}

const PAGE_SIZE = 10

const ModificationHistoryBase: React.FC<BaseProps> = ({
  selectedDate,
  onLeaveButtonClick,
  selected,
  tableName,
  filterFn,
  renderRows,
  title,
}) => {
  const { t } = useTranslation()
  const [auditData, setAuditData] = useState<History[]>([])
  const [isLastPage, setIsLastPage] = useState(false)
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false)

  const monthKey = useMemo(() => selectedDate.format('YYYY-MM'), [selectedDate])
  const startOfMonth = useMemo(() => selectedDate.startOf('month').format(DateFormat.YearMonthDay), [selectedDate])
  const endOfMonth = useMemo(() => selectedDate.endOf('month').format(DateFormat.YearMonthDay), [selectedDate])

  const [requestParams, setRequestParams] = useState<ModificationHistoryParams>({
    'chosen.id': selected?.id || undefined,
    tableName,
    page: 0,
    size: PAGE_SIZE,
    startDate: startOfMonth,
    endDate: endOfMonth,
  })

  const monthRef = useRef(monthKey)
  const keysRef = useRef<Set<string>>(new Set())
  const isPagingRef = useRef(false)

  const {
    data: modificationHistory,
    isSuccess,
    isLoading,
    isError,
    refetch,
  } = useGetModificationHistoryQuery(requestParams, { refetchOnMountOrArgChange: true })

  const makeKey = (it: History) => {
    const n = parseJsonSafe(it.newValue)
    const o = parseJsonSafe(it.oldValue)
    const id = n?.id || o?.id
    return id ? `${id}|${it.createdAt}` : `${it.createdAt}|${it.name}|${it.oldValue}|${it.newValue}`
  }

  useEffect(() => {
    setAuditData([])
    setIsLastPage(false)
    setHasFetchedOnce(false)
    keysRef.current = new Set()
    monthRef.current = monthKey
    isPagingRef.current = false
    setRequestParams(prev => ({
      ...prev,
      'chosen.id': selected?.id,
      tableName,
      page: 0,
      size: PAGE_SIZE,
      startDate: startOfMonth,
      endDate: endOfMonth,
    }))
  }, [selected?.id, tableName, startOfMonth, endOfMonth, monthKey])

  useEffect(() => {
    if (!isSuccess || !modificationHistory) return
    const pageObj: any = modificationHistory as any
    const incoming: History[] = Array.isArray(pageObj) ? pageObj : pageObj?.content ?? []
    const currentMonth = monthKey
    const currentPage = requestParams.page || 0

    if (currentMonth !== monthRef.current) {
      keysRef.current = new Set()
      monthRef.current = currentMonth
      setAuditData([])
    }

    const deduped = incoming.filter(it => {
      const key = makeKey(it)
      if (keysRef.current.has(key)) return false
      keysRef.current.add(key)
      return true
    })

    if (currentPage === 0) {
      setAuditData(deduped)
    } else if (deduped.length) {
      setAuditData(prev => [...prev, ...deduped])
    }

    const hasLastFlag = !Array.isArray(pageObj) && typeof pageObj?.last === 'boolean'
    const computedLast = hasLastFlag ? Boolean(pageObj.last) : incoming.length < (requestParams.size || PAGE_SIZE)
    setIsLastPage(computedLast)
    setHasFetchedOnce(true)
    isPagingRef.current = false
  }, [isSuccess, modificationHistory, requestParams.page, requestParams.size, monthKey])

  const applyFilterParams: Dispatch<SetStateAction<ModificationHistoryParams>> = value => {
    keysRef.current = new Set()
    setAuditData([])
    setIsLastPage(false)
    setHasFetchedOnce(false)
    isPagingRef.current = false
    if (typeof value === 'function') {
      setRequestParams(prev =>
        value({
          ...prev,
          'chosen.id': selected?.id,
          tableName,
          page: 0,
          size: PAGE_SIZE,
          startDate: startOfMonth,
          endDate: endOfMonth,
        })
      )
    } else {
      setRequestParams({
        ...value,
        'chosen.id': selected?.id,
        tableName,
        page: 0,
        size: PAGE_SIZE,
        startDate: startOfMonth,
        endDate: endOfMonth,
      })
    }
  }

  const filteredAuditData = useMemo(() => {
    const opType = requestParams.operationType as OperationType | undefined

    const getOperationType = (item: History): OperationType => {
      const oldDataRaw = parseJsonSafe(item.oldValue)
      const newDataRaw = parseJsonSafe(item.newValue)

      const hasKeys = (obj: any): boolean => obj !== null && typeof obj === 'object' && Object.keys(obj).length > 0

      const isAddition = !hasKeys(oldDataRaw) && hasKeys(newDataRaw)
      const isDeletion = hasKeys(oldDataRaw) && !hasKeys(newDataRaw)

      if (isAddition) return newDataRaw?.vacation ? 'acknowledged' : 'addition'
      if (isDeletion) return oldDataRaw?.vacation ? 'unacknowledged' : 'deletion'
      return 'modification'
    }

    return auditData
      .filter(item => filterFn(item, selectedDate))
      .filter(item => !opType || getOperationType(item) === opType)
  }, [auditData, filterFn, selectedDate, requestParams.operationType])

  const uniqueDateArray = useMemo(() => {
    const uniqueDatesSet = new Set<string>()
    filteredAuditData.forEach(item => uniqueDatesSet.add(dayjs(item.createdAt).format(DateFormat.DayTwoMonthNameYear)))
    const today = Array.from(uniqueDatesSet).filter(date =>
      dayjs(date, DateFormat.DayTwoMonthNameYear).isSame(dayjs(), 'day')
    )
    const rest = Array.from(uniqueDatesSet)
      .filter(date => !dayjs(date, DateFormat.DayTwoMonthNameYear).isSame(dayjs(), 'day'))
      .sort(
        (a, b) =>
          dayjs(b, DateFormat.DayTwoMonthNameYear).valueOf() - dayjs(a, DateFormat.DayTwoMonthNameYear).valueOf()
      )
    return [...today, ...rest]
  }, [filteredAuditData])

  const theme = useTheme()
  const isMediumSize = useMediaQuery(theme.breakpoints.down('md'))
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))

  const isFilteringOn = useMemo(() => {
    const base =
      requestParams['chosen.id'] === (selected?.id || undefined) &&
      requestParams.tableName === tableName &&
      (requestParams.page || 0) === 0 &&
      requestParams.size === PAGE_SIZE &&
      requestParams.startDate === startOfMonth &&
      requestParams.endDate === endOfMonth &&
      !requestParams.operationType
    return !base
  }, [requestParams, selected?.id, tableName, startOfMonth, endOfMonth])

  const isFilteringVisible = useMemo(
    () => filteredAuditData.length > 0 || isFilteringOn,
    [filteredAuditData.length, isFilteringOn]
  )

  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const [filterVisibleDelayed, setFilterVisibleDelayed] = useState(true)

  useEffect(() => {
    if (isFilteringVisible) setFilterVisibleDelayed(true)
    else {
      const timeout = setTimeout(() => setFilterVisibleDelayed(false), 100)
      return () => clearTimeout(timeout)
    }
  }, [isFilteringVisible])

  const handleErrorRefetch = () => {
    keysRef.current = new Set()
    setAuditData([])
    setIsLastPage(false)
    setHasFetchedOnce(false)
    isPagingRef.current = false
    setRequestParams({
      'chosen.id': selected?.id || undefined,
      tableName,
      page: 0,
      size: PAGE_SIZE,
      startDate: startOfMonth,
      endDate: endOfMonth,
      operationType: requestParams.operationType,
    })
    refetch()
  }

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const root = scrollRef.current
    const sentinel = loadMoreRef.current

    if (!root || !sentinel) return
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        if (entry.isIntersecting && !isLoading && !isLastPage && !isPagingRef.current) {
          isPagingRef.current = true
          setRequestParams(prev => ({ ...prev, page: (prev.page || 0) + 1 }))
        }
      },
      { root, rootMargin: '200px', threshold: 0.1 }
    )
    observer.observe(sentinel)
    return () => {
      observer.disconnect()
    }
  }, [isLoading, isLastPage])

  return (
    <Stack
      width={isDownSm ? '350px' : '570px'}
      height="100%"
      sx={{ bgcolor: 'background.main', px: 3, py: 1, minHeight: 0 }}
    >
      {isError ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            p: 2,
            gap: 2,
          }}
        >
          <Typography color="error">{t('page.modification.errorLoadingData')}</Typography>
          <Button onClick={handleErrorRefetch}>{t('page.modification.retry')}</Button>
        </Box>
      ) : isLoading && !hasFetchedOnce ? (
        <Box flex={1} p={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box pt={2} pb={1} display="flex" justifyContent="space-between">
            <Typography component="h3" variant="h4">
              {title}
            </Typography>
            <HistoryIcon />
          </Box>

          <Box pb={2} display="flex" gap="4px">
            <Typography fontWeight="bold">{capitalizeMonthName(selectedDate)}</Typography>
            <Typography>{selectedDate.format(DateFormat.Year)}</Typography>
          </Box>

          {(filteredAuditData.length > 0 || isFilteringOn) && (
            <Button
              sx={{ alignSelf: 'end' }}
              className={isFilterMenuOpen ? 'selected' : undefined}
              variant="contained"
              startIcon={<FilterList />}
              onClick={() => setIsFilterMenuOpen(prev => !prev)}
              color="secondary"
              size="small"
            >
              {t('page.usersProfile.filterTags')}
            </Button>
          )}

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              mt: 2,
              gap: isFilterMenuOpen ? 2 : 0,
              flexDirection: isMediumSize ? 'column-reverse' : 'row',
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            <Box sx={{ flex: 1, overflowY: 'auto', scrollbarGutter: 'stable', minHeight: 0 }} ref={scrollRef}>
              <Box sx={{ pr: 1.5, width: '100%' }}>
                {uniqueDateArray.length ? (
                  uniqueDateArray.map(date => (
                    <React.Fragment key={date}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography component="h4" variant="h6">
                          {dayjs(date, DateFormat.DayTwoMonthNameYear).isSame(dayjs(), 'day')
                            ? t('page.modification.today')
                            : dayjs(date, DateFormat.DayTwoMonthNameYear).format('dddd')}
                        </Typography>
                        <Typography>{date}</Typography>
                      </Stack>
                      {renderRows(date, filteredAuditData, selectedDate)}
                    </React.Fragment>
                  ))
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                      width: '100%',
                    }}
                  >
                    <Typography component="h4" variant="h5" textAlign="center" px={2}>
                      {t('page.modification.nohistory')}
                    </Typography>
                  </Box>
                )}
                {isLoading && hasFetchedOnce && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
                <Box ref={loadMoreRef} sx={{ height: 1 }} />
              </Box>
            </Box>

            {filterVisibleDelayed && (
              <Collapse in={isFilterMenuOpen} orientation={isMediumSize ? 'vertical' : 'horizontal'}>
                <FilterModificationHistoryMenu
                  requestParams={requestParams}
                  setRequestParams={applyFilterParams}
                  selectedDate={selectedDate}
                />
              </Collapse>
            )}
          </Box>

          <Box py={2} display="flex" justifyContent="flex-end">
            <Button onClick={onLeaveButtonClick} color="inherit" sx={{ fontWeight: 'bold', px: 5 }}>
              {t('components.button.leave')}
            </Button>
          </Box>
        </>
      )}
    </Stack>
  )
}

export default ModificationHistoryBase
